import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Search as MagIcon, ThumbsUp, ThumbsDown, Flag, Loader2, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// -----------------------------------------------------------------------------
// Laws · Rights · Duties · Regulations — the citizen reference catalog.
// -----------------------------------------------------------------------------
const CAT_LABEL = {
  fundamental_rights: "Fundamental Rights",
  fundamental_duties: "Fundamental Duties",
  consumer:           "Consumer",
  governance:         "Governance / RTI",
  digital:            "Digital / Cyber",
  employment:         "Employment / Workplace",
  family:             "Family / Women / Elders",
  child:              "Children",
  traffic:            "Traffic / Motor",
  business:           "Business / Tax / IP",
  property:           "Property / Tenancy",
  health:             "Health / Patients",
  environment:        "Environment",
  public_order:       "Public Order / Criminal Code",
};
const KIND_STYLE = {
  right:      "bg-emerald-100 text-emerald-800",
  duty:       "bg-fuchsia-100 text-fuchsia-800",
  regulation: "bg-stone-100 text-stone-800",
  both:       "bg-amber-100 text-amber-800",
};

export default function Laws() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ categories: [], audiences: [] });
  const [cat, setCat] = useState("");
  const [aud, setAud] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);   // slug currently expanded

  useSeo({
    title: "Laws · Rights · Duties · Regulations · find2near",
    description: "Every citizen's quick reference to Indian fundamental rights, fundamental duties, consumer protection, IT/DPDP, RTI, POSH, traffic, environment, family and business regulations.",
    canonical: "/laws",
  });

  useEffect(() => { api.get("/laws/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  // State-specific laws — chip selector at top; fetched lazily on demand.
  const [state, setState] = useState("");                 // "" = central laws only
  const [statesList, setStatesList] = useState([]);
  const [stateItems, setStateItems] = useState([]);
  const [autoState, setAutoState] = useState(null);       // banner "Detected: Mumbai — jump"
  const { loc } = useUserLocation();

  useEffect(() => {
    api.get("/state-laws").then((r) => setStatesList(r.data.states || [])).catch(() => {});
  }, []);

  // GPS auto-detect — map the user's live city to a state chip. Only auto-
  // switches if the user hasn't manually picked yet AND we haven't shown the
  // banner before this session (`sessionStorage`) so revisits don't nag.
  useEffect(() => {
    if (!loc?.city || state) return;
    const CITY_TO_STATE = {
      mumbai: "mumbai", "navi mumbai": "mumbai", thane: "mumbai", pune: "mumbai",
      delhi:  "delhi",  "new delhi":  "delhi",   noida: "delhi",  gurgaon: "delhi", gurugram: "delhi", ghaziabad: "delhi", faridabad: "delhi",
      bengaluru: "bangalore", bangalore: "bangalore", mysuru: "bangalore", mysore: "bangalore",
    };
    const key = (loc.city || "").toLowerCase().trim();
    const s = CITY_TO_STATE[key];
    if (!s || !statesList.includes(s)) return;
    const dismissed = sessionStorage.getItem(`f2n:laws_auto_state:${s}`);
    if (dismissed) return;
    setAutoState({ city: loc.city, state: s });
  }, [loc?.city, statesList, state]);

  useEffect(() => {
    if (!state) { setStateItems([]); return; }
    api.get("/state-laws", { params: { state } })
       .then((r) => setStateItems(r.data.items || []))
       .catch(() => setStateItems([]));
  }, [state]);
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (cat)  params.category = cat;
    if (aud)  params.applies_to = aud;
    if (kind) params.kind = kind;
    if (q)    params.q = q;
    api.get("/laws", { params })
       .then((r) => setItems(r.data))
       .catch(() => setItems([]))
       .finally(() => setLoading(false));
  }, [cat, aud, kind, q]);

  const rights = useMemo(() => items.filter((x) => x.kind === "right"), [items]);
  const duties = useMemo(() => items.filter((x) => x.kind === "duty"),  [items]);
  const rules  = useMemo(() => items.filter((x) => x.kind === "regulation" || x.kind === "both"), [items]);

  const react = async (slug, kindR) => {
    if (!user) { toast.error("Sign in to react"); return; }
    try { await api.post(`/laws/${slug}/react`, { kind: kindR }); toast.success("Thanks!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="laws-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Scale size={12} className="text-emerald-700" /> Laws · Rights · Duties</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Know your rights. <span className="text-emerald-700">Do your duty.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Fundamental Rights & Duties · Consumer · RTI · IT/DPDP · Traffic · Labour · Family · Business — every citizen's cheat-sheet.</p>
        </div>
        <div className="text-xs text-stone-500">{meta.count || items.length} entries · updated Feb 2026</div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-stone-50 rounded-2xl px-3 py-1.5">
          <MagIcon size={14} className="text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles, acts, sections…" className="bg-transparent w-full outline-none text-sm" data-testid="laws-q" />
        </div>
        <Select value={cat || "__all__"} onValueChange={(v) => setCat(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full w-56 h-9 text-xs" data-testid="laws-cat"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories</SelectItem>
            {(meta.categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{CAT_LABEL[c.key] || c.key} · {c.count}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={aud || "__all__"} onValueChange={(v) => setAud(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full w-48 h-9 text-xs" data-testid="laws-aud"><SelectValue placeholder="Any audience" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any audience</SelectItem>
            {(meta.audiences || []).map((a) => <SelectItem key={a.key} value={a.key}>{a.key} · {a.count}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={kind || "__all__"} onValueChange={(v) => setKind(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full w-40 h-9 text-xs" data-testid="laws-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Rights + Duties</SelectItem>
            <SelectItem value="right">Rights only</SelectItem>
            <SelectItem value="duty">Duties only</SelectItem>
            <SelectItem value="regulation">Regulations</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* GPS auto-detect banner — appears when the live city maps cleanly to
          a state chip and hasn't been dismissed this session. */}
      {autoState && !state && (
        <div className="mb-3 rounded-3xl bg-emerald-50 border border-emerald-200 p-3 flex flex-wrap items-center gap-2" data-testid="laws-auto-banner">
          <MapPin size={14} className="text-emerald-700" />
          <div className="text-sm text-emerald-900 flex-1">
            You're in <b>{autoState.city}</b>. Jump to <b className="capitalize">{autoState.state}</b> local rules?
          </div>
          <button onClick={() => { setState(autoState.state); setAutoState(null); }}
                  className="text-[11px] font-black bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-3 py-1.5"
                  data-testid="laws-auto-jump">
            Show me
          </button>
          <button onClick={() => { sessionStorage.setItem(`f2n:laws_auto_state:${autoState.state}`, "1"); setAutoState(null); }}
                  className="text-[11px] font-bold text-stone-500 hover:text-stone-900 rounded-full px-2 py-1">
            Not now
          </button>
        </div>
      )}

      {/* State chips — Delhi / Mumbai / Bangalore-specific supplements */}
      {statesList.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="laws-state-chips">
          <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mr-1">State-specific</span>
          <button onClick={() => setState("")}
                  className={`text-[11px] font-black rounded-full px-3 py-1 border ${!state ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}>
            Central only
          </button>
          {statesList.map((s) => (
            <button key={s} onClick={() => setState(s)}
                    className={`text-[11px] font-black rounded-full px-3 py-1 border capitalize ${state === s ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}
                    data-testid={`state-chip-${s}`}>
              {s}
            </button>
          ))}
        </div>
      )}
      {state && stateItems.length > 0 && (
        <div className="mb-6" data-testid="state-section">
          <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800 mb-2 capitalize">📍 {state} · {stateItems.length} local rule{stateItems.length !== 1 ? "s" : ""}</div>
          <div className="grid gap-2">
            {stateItems.map((x) => (
              <article key={x.slug} className="rounded-3xl bg-amber-50/50 border border-amber-200 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">state · {x.state}</span>
                  <span className="text-[10px] font-mono text-stone-500">{x.reference}</span>
                </div>
                <div className="font-black text-stone-900">{x.title}</div>
                <p className="text-xs text-stone-700 mt-1">{x.summary}</p>
              </article>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No entries match this filter.</div>
      ) : (
        <div className="space-y-8">
          {rights.length > 0 && <Section title="✊ Rights" items={rights} openSlug={open} setOpen={setOpen} onReact={react} />}
          {duties.length > 0 && <Section title="🙌 Duties" items={duties} openSlug={open} setOpen={setOpen} onReact={react} />}
          {rules.length  > 0 && <Section title="📜 Regulations · Acts · Rules" items={rules} openSlug={open} setOpen={setOpen} onReact={react} />}
        </div>
      )}

      <div className="mt-8 text-[11px] text-stone-500 text-center">
        Not legal advice. Always consult a qualified advocate for your specific situation.
        Full official notice → <Link to="/legal" className="text-red-700 font-bold hover:underline">/legal</Link>.
      </div>
    </div>
  );
}

function Section({ title, items, openSlug, setOpen, onReact }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">{title} · {items.length}</div>
      <div className="grid gap-2">
        {items.map((x) => {
          const open = openSlug === x.slug;
          return (
            <article key={x.slug} className="rounded-3xl bg-white border border-stone-200" data-testid={`law-${x.slug}`}>
              <button onClick={() => setOpen(open ? null : x.slug)} className="w-full text-left p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${KIND_STYLE[x.kind]}`}>{x.kind}</span>
                    <span className="text-[10px] font-mono text-stone-500">{x.reference}</span>
                    {(x.applies_to || []).slice(0, 3).map((a) => (
                      <span key={a} className="text-[10px] font-bold bg-stone-100 rounded-full px-2 py-0.5 text-stone-700">{a}</span>
                    ))}
                  </div>
                  <div className="font-black text-stone-900">{x.title}</div>
                  {x.summary && <p className={`text-xs text-stone-600 mt-1 ${open ? "" : "line-clamp-2"}`}>{x.summary}</p>}
                </div>
                <ChevronRight size={16} className={`text-stone-400 mt-1 transition-transform ${open ? "rotate-90" : ""}`} />
              </button>
              {open && x.details && (
                <div className="px-4 pb-4 -mt-1">
                  <p className="text-sm text-stone-700 whitespace-pre-line leading-relaxed border-t border-stone-100 pt-3">{x.details}</p>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <button onClick={() => onReact(x.slug, "up")}   className="rounded-full px-2 py-1 hover:bg-emerald-50 text-emerald-700 font-bold inline-flex items-center gap-1" data-testid={`up-${x.slug}`}><ThumbsUp size={12}/> Helpful {x.reactions?.up || 0}</button>
                    <button onClick={() => onReact(x.slug, "down")} className="rounded-full px-2 py-1 hover:bg-stone-50 text-stone-600 font-bold inline-flex items-center gap-1"><ThumbsDown size={12}/> Not really {x.reactions?.down || 0}</button>
                    <button onClick={() => onReact(x.slug, "flag")} className="rounded-full px-2 py-1 hover:bg-amber-50 text-amber-700 font-bold inline-flex items-center gap-1 ml-auto"><Flag size={12}/> Suggest fix</button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
